import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { ApolloError, useQuery, useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";

import { AllstarUser, MontageSession } from "../../State";
import {
  CLIPS,
  FINAL_TOUCHES,
  LOADOUT_QUERY,
  MY_BALANCE,
  SINGLE_MONTAGE,
  STUDIO_CARDS,
} from "../../GraphQL/montage";
import { EView } from "./index";
import { Montage } from "../../Model/Montage";
import { GAMES, TLoadoutsQuery } from "./@types";
import { EClipsViews } from "../../@types";
import { analytics } from "MVVM/Analytics";

export const MontageViewModel = () => {
  const [montageSession, setMontageSession] = useRecoilState(MontageSession);
  const [allstarUser] = useRecoilState(AllstarUser);
  const [error, setError] = useState<ApolloError | null>(null);
  const [isFreeUser] = useState(allstarUser?.user?.userGroup?.type === "FREE");

  const montageModel = new Montage();
  const history = useHistory();

  useEffect(() => {
    if (montageSession.view === EView.Music) {
      getLoadouts({
        variables: {
          game: montageSession.game,
          type: "montage",
        },
      });
      getMusic();
    }
    if (montageSession.view === EView.Loadout) {
      getFinalTouches();
    }
  }, [montageSession.view]); //eslint-disable-line

  useEffect(() => {
    setMontageSession({ ...montageSession, isFreeUser: isFreeUser });
  }, []); //eslint-disable-line

  const { error: balanceError } = useQuery(MY_BALANCE, {
    fetchPolicy: "network-only",
    onError() {
      analytics.track("Career Montage - Web Error", {
        source: "MY_BALANCE query",
      });
    },
    onCompleted: (data) => {
      setMontageSession({
        ...montageSession,
        isFreeUser:
          !data.myLedgerBalance.enableProFeatures &&
          allstarUser?.user?.userGroup?.type === "FREE",
        enableProFeatures: data.myLedgerBalance.enableProFeatures,
        hasBalance: !data.myLedgerBalance.block,
      });
    },
  });

  const [getMusic, { loading: musicLoading, data: musicData }] = useLazyQuery(
    STUDIO_CARDS,
    {
      variables: {
        type: ["Music"],
      },
      onError() {
        analytics.track("Career Montage - Web Error", {
          source: "STUDIO_CARDS query",
        });
      },
    },
  );

  const [getLoadouts, { loading: loadoutsLoading, data: loadoutsData }] =
    useLazyQuery<TLoadoutsQuery>(LOADOUT_QUERY, {
      onError() {
        analytics.track("Career Montage - Web Error", {
          source: "LOADOUT_QUERY query",
        });
      },
    });

  const [
    getFinalTouches,
    { loading: finalTouchesLoading, data: finalTouchesData },
  ] = useLazyQuery(FINAL_TOUCHES, {
    variables: {
      type: ["Memes"],
      audioType: ["Audio FX"],
      mechanic: ["Camera Angle"],
      memeMechanic: ["Montage"],
    },
    onError(error) {
      setError(error);
      analytics.track("Career Montage - Web Error", {
        source: "FINAL_TOUCHES query",
      });
    },
    onCompleted: () => {},
  });

  const [getClips, { loading: clipsLoading, data: clipsData }] = useLazyQuery(
    CLIPS,
    {
      fetchPolicy: "network-only",
      onError(error) {
        setError(error);
        analytics.track("Career Montage - Web Error", {
          source: "CLIPS query",
        });
      },
    },
  );

  const MIN_POLLING_TIME = 10;
  const MAX_POLLING_TIME = 90;
  const {
    loading: montageLoading,
    data: montageData,
    startPolling,
    stopPolling,
  } = useQuery(SINGLE_MONTAGE, {
    skip: !montageSession.montageId,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (montageSession.montagePolling.isPolling) {
        const secondsPolling =
          (Date.now() - montageSession.montagePolling.startedPolling) / 1000;

        if (secondsPolling > MIN_POLLING_TIME) {
          if (data?.getMontage?.originalClips) {
            stopPolling();
            history.push(`/profile?view=${EClipsViews.Montages}`);
          } else if (secondsPolling > MAX_POLLING_TIME) {
            stopPolling();
            setMontageSession({ ...montageSession, view: EView.Error });
          }
        }
      }
    },
    onError(error) {
      setError(error);
      analytics.track("Career Montage - Web Error", {
        source: "SINGLE_MONTAGE query",
      });
    },
    variables: { clipIdentifier: montageSession.montageId },
  });

  if (balanceError) {
    setError(balanceError);
  }

  return {
    allstarUser,
    montageSession,
    setMontageSession,
    error,
    clips: { clipsData, clipsLoading, getClips },
    music: { musicData, musicLoading },
    loadouts: { loadoutsData, loadoutsLoading },
    finalTouches: {
      finalTouchesData: [
        {
          description: "Remove Watermark",
          cards: [{ _id: "60a6d86a8c4fd555132afc97" }],
          pro: true,
          game: [GAMES.CSGO, GAMES.CounterStrike2],
        },
        {
          description: "Enable Multiple Camera Angles",
          cards: finalTouchesData?.cameraCards?.cards,
          pro: true,
          game: [GAMES.CSGO],
        },
        {
          description: "Enable Gun Cam",
          cards: [{ _id: "60a6d86a8c4fd555132afcb4" }],
          pro: false,
          game: [GAMES.CSGO],
        },
        {
          description: "Hide Kill Feed",
          pro: false,
          cards: [{ _id: "622be8b4ff38f13a9d241322" }],
          game: [GAMES.CSGO],
        },
        {
          description: "Hide Crosshair",
          pro: false,
          cards: [{ _id: "622be70eff38f13a9d241312" }],
          game: [GAMES.CSGO, GAMES.CounterStrike2],
        },
        {
          description: "Mute Voice Comms",
          pro: false,
          cards: [{ _id: "61e0912ab4a61f0e6138e801" }],
          game: [GAMES.CSGO],
        },
        {
          description: "Enable Random Meme FX",
          cards: finalTouchesData?.audioEffects?.cards,
          pro: false,
          game: [GAMES.CSGO, GAMES.CounterStrike2],
        },
        {
          description: "Enable Random Meme Overlays",
          pro: false,
          cards: finalTouchesData?.memes?.cards,
          game: [GAMES.CSGO, GAMES.CounterStrike2],
        },
      ],
      finalTouchesLoading,
    },
    montage: { startPolling, stopPolling, montageLoading, montageData },
    review: { montageModel },
  };
};
